import React from 'react'
import _ from 'lodash'
import {App, message as antdMessage} from 'antd'
import {renderSelf} from 'views/Shared'
import {MaterialTypes} from '../enums'
import {NotificationActionTypes} from './createNotification'

// ─────────────────────────────────────────────
// Core: build content từ action + params
// (tái sử dụng logic giống createNotification)
// ─────────────────────────────────────────────
const buildMessageContent = ({action, params = {}, translate = renderSelf}) => {
    if (!action) return null

    const {withParams = renderSelf, ...rest} = action
    const {message, description} = withParams({...rest, ...params}) || {}

    // antd message chỉ có 1 dòng content — ưu tiên description nếu có, fallback message
    const text = description ?? message
    if (!text) return null

    return (
        <span className="capitalize-first">
      {_.isString(text) ? translate(text) : text}
    </span>
    )
}

// ─────────────────────────────────────────────
// Map MaterialTypes → antd message method name
// ─────────────────────────────────────────────
const typeToMethod = {
    [MaterialTypes.SUCCESS]: 'success',
    [MaterialTypes.ERROR]: 'error',
    [MaterialTypes.WARNING]: 'warning',
    [MaterialTypes.INFO]: 'info',
}

// ─────────────────────────────────────────────
// 1) Static API  —  dùng cho global / outside component
//    antd 5: cần gọi antdMessage.config() hoặc
//    App.useApp() nếu muốn đúng context,
//    nhưng static method vẫn hoạt động cho phần lớn TH.
// ─────────────────────────────────────────────
export const createMessageNotification = ({
                                              params = {},
                                              translate = renderSelf,
                                              type = MaterialTypes.SUCCESS,
                                              action = NotificationActionTypes.CREATE,
                                              duration = 3,
                                              ...props
                                          }) => {
    const content = buildMessageContent({action, params, translate})
    if (!content) return

    const method = typeToMethod[type] ?? 'info'
    antdMessage[method]({content, duration, ...props})
}

export const successMessage = (
    action = NotificationActionTypes.CREATE,
    translate = renderSelf,
    params = {}
) =>
    createMessageNotification({
        action,
        params,
        translate,
        type: MaterialTypes.SUCCESS,
    })

export const informationMessage = (
    action = NotificationActionTypes.INFORMATION,
    translate = renderSelf,
    params = {}
) =>
    createMessageNotification({
        action,
        params,
        translate,
        type: MaterialTypes.INFO,
    })

export const warningMessage = (
    action = NotificationActionTypes.WARNING,
    translate = renderSelf,
    params = {}
) =>
    createMessageNotification({
        action,
        params,
        translate,
        type: MaterialTypes.WARNING,
    })

export const errorMessage = (
    translate = renderSelf,
    params = {errorType: undefined}
) =>
    createMessageNotification({
        params,
        translate,
        type: MaterialTypes.ERROR,
        action: NotificationActionTypes.ERROR,
    })

export const useMessageNotification = () => {
    const {message: messageApi} = App.useApp()

    const showMessage = ({
                             params = {},
                             translate = renderSelf,
                             type = MaterialTypes.SUCCESS,
                             action = NotificationActionTypes.CREATE,
                             duration = 3,
                             ...props
                         }) => {
        const content = buildMessageContent({action, params, translate})
        if (!content) return

        const method = typeToMethod[type] ?? 'info'
        messageApi[method]({content, duration, ...props})
    }

    return {
        showMessage,

        successMessage: (
            action = NotificationActionTypes.CREATE,
            translate = renderSelf,
            params = {}
        ) => showMessage({action, params, translate, type: MaterialTypes.SUCCESS}),

        informationMessage: (
            action = NotificationActionTypes.INFORMATION,
            translate = renderSelf,
            params = {}
        ) => showMessage({action, params, translate, type: MaterialTypes.INFO}),

        warningMessage: (
            action = NotificationActionTypes.WARNING,
            translate = renderSelf,
            params = {}
        ) => showMessage({action, params, translate, type: MaterialTypes.WARNING}),

        errorMessage: (
            translate = renderSelf,
            params = {errorType: undefined}
        ) =>
            showMessage({
                params,
                translate,
                type: MaterialTypes.ERROR,
                action: NotificationActionTypes.ERROR,
            }),
    }
}